import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@resource-kit/ui/react';
import type { AnalyticsMetrics } from './analyticsTypes';
import { formatMoney } from './analyticsFormat';

function MiniBar({ value, label }: { value: number; label: string }) {
  const bounded = Math.max(0, Math.min(1, value));
  return (
    <div role="img" aria-label={label} className="h-2 overflow-hidden rounded-full bg-muted">
      <div className="h-full rounded-full bg-primary" style={{ width: `${bounded * 100}%` }} />
    </div>
  );
}

export function MetricsPanel({ metrics }: { metrics: AnalyticsMetrics }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const number = (value: number) =>
    new Intl.NumberFormat(locale, {
      maximumFractionDigits: 2,
    }).format(value);
  const percent = (value: number | null) =>
    value === null
      ? t('analytics.value.notAvailable')
      : new Intl.NumberFormat(locale, {
          style: 'percent',
          maximumFractionDigits: 1,
        }).format(value);
  const growthRows = [
    { label: t('analytics.metrics.orders'), value: metrics.growth.orders },
    { label: t('analytics.metrics.revenue'), value: metrics.growth.revenue },
    { label: t('analytics.metrics.orderingCustomers'), value: metrics.growth.ordering_customers },
  ];

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>{t('analytics.metrics.growth')}</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('analytics.metrics.metric')}</TableHead>
                <TableHead>{t('analytics.metrics.current')}</TableHead>
                <TableHead>{t('analytics.metrics.previousPeriod')}</TableHead>
                <TableHead>{t('analytics.metrics.periodChange')}</TableHead>
                <TableHead>{t('analytics.metrics.previousYear')}</TableHead>
                <TableHead>{t('analytics.metrics.yearChange')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {growthRows.map((row) => (
                <TableRow key={row.label}>
                  <TableCell>{row.label}</TableCell>
                  <TableCell>{number(row.value.current)}</TableCell>
                  <TableCell>{number(row.value.previous_period)}</TableCell>
                  <TableCell>{percent(row.value.period_over_period_change)}</TableCell>
                  <TableCell>{number(row.value.previous_year)}</TableCell>
                  <TableCell>{percent(row.value.year_over_year_change)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('analytics.metrics.activation')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <p>
              {t('analytics.metrics.cohortRange', {
                start: metrics.activation.cohort_from,
                end: metrics.activation.cohort_to,
              })}
            </p>
            <p>
              {t('analytics.metrics.attained', {
                attained: number(metrics.activation.attained_customers),
                total: number(metrics.activation.cohort_customers),
              })}
            </p>
            <MiniBar
              value={metrics.activation.attainment_rate}
              label={t('analytics.metrics.activationRate', {
                value: percent(metrics.activation.attainment_rate),
              })}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>{t('analytics.metrics.matching')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <p>
              {t('analytics.metrics.matchingCounts', {
                covered: number(metrics.matching.covered_customers),
                active: number(metrics.matching.active_customers),
                dishes: number(metrics.matching.offered_dishes),
              })}
            </p>
            <MiniBar
              value={metrics.matching.coverage_rate}
              label={t('analytics.metrics.coverageRate', {
                value: percent(metrics.matching.coverage_rate),
              })}
            />
            <p className="text-sm text-muted-foreground">
              {t('analytics.metrics.uncovered', {
                count: number(metrics.matching.uncovered_customers),
              })}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('analytics.metrics.retention')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 overflow-x-auto">
          <p>
            {t('analytics.metrics.activeCore', {
              count: number(metrics.retention.active_core_customers),
            })}
          </p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('analytics.metrics.checkpoint')}</TableHead>
                <TableHead>{t('analytics.metrics.atRisk')}</TableHead>
                <TableHead>{t('analytics.metrics.eventualReturn')}</TableHead>
                <TableHead>{t('analytics.metrics.rate')}</TableHead>
                <TableHead>{t('analytics.metrics.miniChart')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {metrics.retention.survival_curve.map((point) => (
                <TableRow key={point.checkpoint_days}>
                  <TableCell>
                    {t('analytics.metrics.days', { count: point.checkpoint_days })}
                  </TableCell>
                  <TableCell>{number(point.at_risk)}</TableCell>
                  <TableCell>{number(point.eventual_return)}</TableCell>
                  <TableCell>{percent(point.rate)}</TableCell>
                  <TableCell className="min-w-32">
                    <MiniBar value={point.rate} label={percent(point.rate)} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('analytics.metrics.alerts')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            <p>
              {t('analytics.metrics.watchlist', { count: number(metrics.alerts.watchlist_size) })}
            </p>
            <p>
              {t('analytics.metrics.lifetimeSpend', {
                value: formatMoney(metrics.alerts.historical_contribution_amount, locale),
              })}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>{t('analytics.metrics.leverage')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            <p>
              {t('analytics.metrics.orderCount', { count: number(metrics.leverage.order_count) })}
            </p>
            <p>
              {t('analytics.metrics.itemCount', { count: number(metrics.leverage.item_count) })}
            </p>
            <p>
              {t('analytics.metrics.averageItems', {
                value: number(metrics.leverage.average_items_per_order),
              })}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
